import React, { useMemo, useState } from "react";
import axios from "../../api/axios";
import "./CreateContractModal.css";

export default function CreateContractModal({
  isOpen,
  onClose,
  locale = "kz",
  token,
  onCreated = () => {},
}) {
  const t = useMemo(() => ({
    kz: {
      title: "Келісім шарт жасау",
      orderId: "Бұйрық ID",
      load: "Сарапшыларды жүктеу",
      experts: "Сыртқы сарапшылар",
      amount: "Сома (₸)",
      number: "Договор нөмірі (қаласаңыз)",
      create: "Жасау",
      cancel: "Жабу",
      empty: "Сарапшылар табылмады",
      loading: "Жүктелуде...",
      error: "Қате орын алды",
      fillRequired: "Бұйрық ID, Сарапшы және Сома толтырылсын",
    },
    ru: {
      title: "Создать договор",
      orderId: "ID приказа",
      load: "Загрузить экспертов",
      experts: "Внешние эксперты",
      amount: "Сумма (₸)",
      number: "Номер договора (необязательно)",
      create: "Создать",
      cancel: "Закрыть",
      empty: "Эксперты не найдены",
      loading: "Загрузка...",
      error: "Произошла ошибка",
      fillRequired: "Нужны ID приказа, эксперт и сумма",
    },
  })[locale], [locale]);

  const langHeader = useMemo(
    () => ({ "Accept-Language": locale === "kz" ? "kk" : "ru" }),
    [locale]
  );

  const [orderId, setOrderId] = useState("");
  const [experts, setExperts] = useState([]);         // [{ _id, name, email, ... }]
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [selectedExpertId, setSelectedExpertId] = useState(null);
  const [amount, setAmount] = useState("");
  const [contractNumber, setContractNumber] = useState("");

  // нормализация экспертов к единому виду
  const normalizeExperts = (arr) =>
    (arr || [])
      .filter(Boolean)
      .map((ex) => ({
        ...ex,
        _id: ex?.external_expert_id ?? ex?.id,             // <- главный ключ
        name: ex?.name || ex?.full_name || ex?.fio || "—",
        email: ex?.email || ex?.user?.email || "",
        phone: ex?.phone || "",
      }))
      .filter((ex) => ex._id != null);

  const loadExperts = async () => {
    if (!orderId) return;
    setLoading(true);
    setErr("");
    setExperts([]);
    setSelectedExpertId(null);
    try {
      const res = await axios.get(`/orders/${orderId}/external-experts`, {
        headers: { Authorization: `Bearer ${token}`, ...langHeader },
      });
      const raw =
        res?.data?.experts ??
        res?.data?.data?.experts ??
        res?.data?.data ??                             // на всякий случай
        [];
      setExperts(normalizeExperts(raw));
    } catch (e) {
 
      setErr(t.error);
    } finally {
      setLoading(false);
    }
  };

  const parseMoney = (v) => {
    const num = Number(String(v ?? "").replace(/[^\d.-]/g, ""));
    return Number.isFinite(num) ? num : undefined;
  };

  const handleCreate = async () => {
    if (!orderId || !selectedExpertId || !parseMoney(amount)) {
      setErr(t.fillRequired);
      return;
    }
    try {
      setLoading(true);
      setErr("");

      // TODO: проверь точное имя эндпоинта/полей на бэке
      // Пример:
      // await axios.post(
      //   "/contracts",
      //   {
      //     order_id: Number(orderId),
      //     external_expert_id: Number(selectedExpertId),
      //     amount: parseMoney(amount),
      //     contract_number: contractNumber || undefined,
      //   },
      //   { headers: { Authorization: `Bearer ${token}`, ...langHeader } }
      // );

      // демо-успех
      await new Promise((r) => setTimeout(r, 200));
      onCreated();
      onClose();
    } catch (e) {

      setErr(t.error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="ccm-backdrop" onClick={onClose}>
      <div className="ccm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ccm-header">
          <h3>{t.title}</h3>
          <button className="ccm-x" onClick={onClose}>×</button>
        </div>

        <div className="ccm-body">
          <div className="ccm-row">
            <label className="ccm-label">{t.orderId}</label>
            <div className="ccm-inline">
              <input
                className="ccm-input"
                value={orderId}
                onChange={(e) => setOrderId(e.target.value.replace(/[^\d]/g, ""))}
                placeholder="90"
              />
              <button className="ccm-btn" onClick={loadExperts} disabled={!orderId || loading}>
                {t.load}
              </button>
            </div>
          </div>

          {loading && <div className="ccm-note">{t.loading}</div>}
          {!!err && <div className="ccm-error">{err}</div>}

          <div className="ccm-row">
            <label className="ccm-label">{t.experts}</label>
            <div className="ccm-experts">
              {experts.length === 0 && !loading ? (
                <div className="ccm-note">{t.empty}</div>
              ) : (
                experts.map((ex) => (
                  <label key={ex._id} className={`ccm-expert ${selectedExpertId === ex._id ? "active" : ""}`}>
                    <input
                      type="radio"
                      name="expert"
                      checked={selectedExpertId === ex._id}
                      onChange={() => setSelectedExpertId(ex._id)}
                    />
                    <div className="ccm-expert-info">
                      <div className="ccm-expert-name">{ex.name}</div>
                      <div className="ccm-expert-sub">
                        {ex.email || "—"} {ex.phone ? ` · ${ex.phone}` : ""}
                      </div>
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>

          <div className="ccm-grid">
            <div className="ccm-row">
              <label className="ccm-label">{t.amount}</label>
              <input
                className="ccm-input"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="150000"
                inputMode="numeric"
              />
            </div>
            <div className="ccm-row">
              <label className="ccm-label">{t.number}</label>
              <input
                className="ccm-input"
                value={contractNumber}
                onChange={(e) => setContractNumber(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="ccm-footer">
          <button className="ccm-btn ghost" onClick={onClose}>{t.cancel}</button>
          <button className="ccm-btn primary" onClick={handleCreate} disabled={loading}>
            {t.create}
          </button>
        </div>
      </div>
    </div>
  );
}
